import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { 
  Mail, Edit2, X, Loader2, Save, Eye, RotateCcw, Check, Info
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useBranding } from '../../contexts/BrandingContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATE_ICONS = {
  welcome: '👋',
  appointment_confirmation: '📅',
  appointment_reminder: '⏰',
  membership_welcome: '🎉',
  membership_renewal: '🔄',
  gift_card: '🎁',
  special_offer: '🏷️',
  birthday: '🎂'
};

const AdminEmailTemplatesPage = () => {
  const { toast } = useToast();
  const { branding } = useBranding();
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);

  const [formData, setFormData] = useState({
    type: '', name: '', subject: '', body: '', description: '', placeholders: [], active: true
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/email-templates?practiceId=demo-practice-001`);
      const data = await response.json();
      setTemplates(data);
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      type: template.type,
      name: template.name,
      subject: template.subject,
      body: template.body,
      description: template.description || '',
      placeholders: template.placeholders || [],
      active: template.active !== false
    });
    setPreviewContent(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        ...formData,
        practiceId: 'demo-practice-001'
      };

      const response = await fetch(`${API}/email-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!response.ok) throw new Error('Failed to save template');
      toast({ title: 'Template Saved', description: `${formData.name} has been updated.` });
      setEditingTemplate(null);
      fetchTemplates();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save template.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const response = await fetch(`${API}/email-templates/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: formData.subject,
          body: formData.body
        })
      });

      const data = await response.json();
      setPreviewContent(data);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to generate preview.', variant: 'destructive' });
    } finally {
      setPreviewing(false);
    }
  };

  const handleReset = async (templateType) => {
    if (!window.confirm('Reset this template to default? Your customizations will be lost.')) return;
    try {
      await fetch(`${API}/email-templates/reset/${templateType}?practiceId=demo-practice-001`, {
        method: 'POST'
      });
      toast({ title: 'Template Reset', description: 'Template has been reset to default.' });
      setEditingTemplate(null);
      fetchTemplates();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to reset template.', variant: 'destructive' });
    }
  };

  const insertPlaceholder = (placeholder) => {
    const textarea = document.getElementById('template-body');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.body;
      const before = text.substring(0, start);
      const after = text.substring(end);
      setFormData({
        ...formData,
        body: before + `{${placeholder}}` + after
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-email-templates">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Mail className="h-6 w-6 text-blue-500" />
          Email Templates
        </h1>
        <p className="text-gray-600">Customize automated email messages sent to your clients</p>
      </div>

      {editingTemplate ? (
        // Edit Mode
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Editor */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <span>{TEMPLATE_ICONS[formData.type] || '📧'}</span>
                  Edit: {formData.name}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setEditingTemplate(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>{formData.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="template-subject">Subject Line</Label>
                <Input
                  id="template-subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="template-body">Email Body</Label>
                <Textarea
                  id="template-body"
                  value={formData.body}
                  onChange={(e) => setFormData({...formData, body: e.target.value})}
                  rows={15}
                  className="mt-1 font-mono text-sm"
                />
              </div>

              {/* Placeholders */}
              <div>
                <Label className="mb-2 block">Available Placeholders</Label>
                <div className="flex flex-wrap gap-2">
                  {formData.placeholders.map((p) => (
                    <Button
                      key={p}
                      variant="outline"
                      size="sm"
                      onClick={() => insertPlaceholder(p)}
                      className="text-xs"
                    >
                      {`{${p}}`}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">Click to insert at cursor position</p>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => handleReset(formData.type)}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset to Default
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handlePreview} disabled={previewing}>
                    {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                    Preview
                  </Button>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Preview</CardTitle>
              <CardDescription>How the email will look with sample data</CardDescription>
            </CardHeader>
            <CardContent>
              {previewContent ? (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 p-4 border-b">
                    <p className="text-sm text-gray-500">Subject:</p>
                    <p className="font-semibold">{previewContent.subject}</p>
                  </div>
                  <div className="p-4 bg-white">
                    <pre className="whitespace-pre-wrap font-sans text-sm">{previewContent.body}</pre>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Eye className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>Click "Preview" to see how your email will look</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        // List Mode
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <Card key={template.type} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{TEMPLATE_ICONS[template.type] || '📧'}</span>
                    <div>
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      {template.isCustom && (
                        <Badge className="bg-blue-100 text-blue-800 text-xs mt-1">Customized</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500 mb-4">{template.description}</p>
                <div className="bg-gray-50 rounded p-3 mb-4">
                  <p className="text-xs text-gray-500 mb-1">Subject:</p>
                  <p className="text-sm font-medium truncate">{template.subject}</p>
                </div>
                <Button variant="outline" className="w-full" onClick={() => handleEdit(template)}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Customize
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4 flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800 font-medium">About Email Templates</p>
            <p className="text-sm text-blue-700">
              Placeholders like {'{client_name}'} and {'{spa_name}'} are automatically replaced with real data when emails are sent. 
              Make sure to keep the curly braces around placeholder names.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminEmailTemplatesPage;
